//Oct28 PublishedFormPage.jsx:
//Oct21 PublishedFormPage.jsx:
//july 17 PublishedFormPage.jsx:
//May1 PublishedFormPage:
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Paper, Box, ThemeProvider, Typography, Snackbar, Alert } from "@mui/material";
import NavbarComponent from "../components/Navbar/NavbarComponent";

import { useFormContext } from "../contexts/FormContext";
import { useFieldMetadata } from "../contexts/FieldMetadataContext";
import { useFormTheme } from "../contexts/FormThemeContext";
import { useFormLayout } from "../contexts/FormLayoutContext";
import { useMultiAnswerField } from "../contexts/MultiAnswerFieldContext";
import { useFieldEdit } from "../contexts/FieldEditContext";
import { useAssetMetadata } from "../contexts/AssetMetadataContext";
import { useFormAnswers } from "../contexts/FormAnswersContext";
import { tagAssetWithForm, recordAssetIngestEvent } from "../utils/frontend_assetManager";
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storageUtils';

import PreviewFields from "../components/FormSettings/PreviewFields"; // Import the new PreviewFields component
import SubmitButtonComponent from "../components/FormSettings/FormDesignLayout/SubmitButtonComponent"; // ✅ Import Submit Button
import { useFormSubmissionSettings } from "../contexts/FormSubmissionSettingsContext"; // Import submission settings context
import FormPreviewSkeleton from "../components/FormSettings/FormPreviewSkeleton"; // 🔹 Our Skeleton component
import { useFormSubmissions } from "../contexts/FormSubmissionsContext";

import { useAuth } from "../contexts/AuthContext";

import DisplayCardPreview  from "../components/FormSettings/FormSubmitSettings/DisplayCardPreview";
import DisplayTablePreview from "../components/FormSettings/FormSubmitSettings/DisplayTablePreview";
//import SessionSubmissionTable from "../components/FormSettings/FormSubmitSettings/SessionSubmissionTable";

/**
 * PublishedFormPage renders a full preview of the **published** form using the MUI theme
 * from FormThemeContext. It waits for:
 *   - formId to be set + formIdReady === true
 *   - all metadata to load (metadataLoading === false)
 *   - layout to load (isLoading === false)
 *   - theme to load (themeLoading === false)
 *   - dropdown options (dropdownLoading === false)
 * If any are not ready, it shows a skeleton instead.
 */
const PublishedFormPage = () => {
  // From FormContext, now also pulling formDetails (which should have isPublic)
  const { formId, formIdReady, formDetails } = useFormContext();

  const { fieldsMetadata, isLoading: metadataLoading } = useFieldMetadata();
  const { layoutConfig, fetchLatestLayout, isLoading: layoutIsLoading } = useFormLayout();
  const { loading: dropdownLoading, dropdownOptions } = useMultiAnswerField();
  const { fieldChanges, validationErrors } = useFieldEdit();
  const { submitAssetFromForm } = useAssetMetadata();
  const { buildSubmissionArray, setAnswer } = useFormAnswers();


  const { muiTheme, selectedTheme, themeLoading } = useFormTheme();
   const {
     getSubmissionUIComponent,
     isDisplayInCardEnabled,   // ← grab it in the same call
   } = useFormSubmissionSettings();
  const { handleSubmit, submissions, loading, submitStatus } = useFormSubmissions();

// Get currentUser to check if a user is logged in
const { currentUser, organizationId: orgIdFromAuth, initialized, authLoading } = useAuth();

 // When AUF is opened as a dialog, Index2 passes state.modalFrom via navigate()
 const location = useLocation();
 const inModal = Boolean(location.state && location.state.modalFrom);
 const navigate = useNavigate();   //back close for AUF modal

const isPublicPreview = (() => {
  if (typeof window === "undefined") return false;
  const raw = window.sessionStorage.getItem("isPublicPreview");
  try { return raw ? JSON.parse(raw).value === true : false; }
  catch { return false; }             // malformed JSON → treat as false
})();

  // Local state controlling final readiness
  const [isReady, setIsReady] = useState(false);
  
 // ────────────────────────────────────────────────────────────────
 // DEBUG test AUF Flicker Diagnostics (LOG ONLY)
 // Enable in DevTools:
 //   window.__DEBUG_AUF_FLICKER__ = true
 // ────────────────────────────────────────────────────────────────
 const __DBG__ =
   typeof window !== "undefined" && window.__DEBUG_AUF_FLICKER__ === true;

 const renderCountRef = useRef(0);
 const lastRenderAtRef = useRef(0);
 renderCountRef.current += 1;

 // For identity tracking (helps detect "same content, new object")
 const lastLayoutRef = useRef(null);
 const lastMetaRef = useRef(null);

 if (__DBG__) {
   const now = Date.now();
   const since = lastRenderAtRef.current ? (now - lastRenderAtRef.current) : null;
   lastRenderAtRef.current = now;

   const layoutSameIdentity = lastLayoutRef.current === layoutConfig;
   const metaSameIdentity = lastMetaRef.current === fieldsMetadata;

   console.log("[AUF:Flicker][PublishedFormPage] render", {
     tick: renderCountRef.current,
     msSinceLastRender: since,
     formId,
     inModal,
     isPublicPreview,
     isAssetUploadForm: formDetails?.isAssetUploadForm === true,
     // identity checks (super important)
     layoutSameIdentity,
     metaSameIdentity,
     layoutRows: layoutConfig ? Object.keys(layoutConfig).length : 0,
     metaKeys: fieldsMetadata ? Object.keys(fieldsMetadata).length : 0,
     loading: {
       formIdReady,
       metadataLoading,
       layoutIsLoading,
       themeLoading,
       dropdownLoading,
     },
     isReady,
   });

   lastLayoutRef.current = layoutConfig;
   lastMetaRef.current = fieldsMetadata;
 }

  // For optional debugging logs about submission state:

  // For optional debugging logs about submission state:
//  useEffect(() => {
//    console.log("[PublishedFormPage] SubmissionsContext Access:", {
//      handleSubmitExists: typeof handleSubmit === "function",
//      submissions,
//      loading,
//      submitStatus,
//    });
//  }, [submissions, loading, submitStatus, handleSubmit]);



// AUF-only: submitting flag used to drive loading UI in the button
const [isSubmitting, setIsSubmitting] = useState(false);
// prevent stale unfreeze timers from firing during a later submit attempt
const unfreezeTimerRef = useRef(null);

// Snackbar (non-blocking) user feedback
const [toastOpen, setToastOpen] = useState(false);
const [toastMsg, setToastMsg] = useState("");
const [toastSeverity, setToastSeverity] = useState("info");

// small helper to show messages
const showToast = (msg, severity = "info") => {
  setToastMsg(msg);
  setToastSeverity(severity);
  setToastOpen(true);
};


  /**
   * A single effect that checks all readiness conditions.
   * If all contexts are done loading, we fetch the layout (if needed), then set isReady = true.
   * Otherwise, we set isReady = false.
   */
   useEffect(() => {
    console.log("[PublishedFormPage] Checking unified readiness conditions:", {
      formId,
      formIdReady,
      metadataLoading,
      layoutIsLoading,
      themeLoading,
      dropdownLoading,
    });

  console.log("[PublishedFormPage] gate-check (effect)", {
    formId,
    formIdReady,
    initialized,
    authLoading,
    isReady,
    metadataLoading,
    layoutIsLoading,
    themeLoading,
    dropdownLoading,
    isPublicPreview,
    inModal,
    isAssetUploadForm: formDetails?.isAssetUploadForm === true,
  });

    // ────────────────────────────────────────────────────────────
    // NEW: Instant-ready path for AUF when shown inside the modal
    // This prevents the skeleton and fetches from delaying first paint.
    // ────────────────────────────────────────────────────────────
    if (inModal && formDetails?.isAssetUploadForm === true) {
      setIsReady(true);
      // IMPORTANT: skip fetchLatestLayout() for dialog AUF to avoid flicker
      return;
    }

    // Must have a formId, and (outside headless preview) a ready flag
    if (!formId || (!isPublicPreview && !formIdReady)) {
      console.log("❌ Not ready: Missing or not-ready formId");
      setIsReady(false);
      return;
    }

    // In headless preview we don't wait for every context
    const contextsLoaded =
      !metadataLoading && !layoutIsLoading && !themeLoading && !dropdownLoading;

    if (isPublicPreview || contextsLoaded) {
      console.log("✅ All contexts loaded, fetching layout & marking isReady = true...");

      if (__DBG__) {
        console.log("[AUF:Flicker][PublishedFormPage] readiness effect -> contextsLoaded", {
          formId,
          isPublicPreview,
          contextsLoaded,
          willCallFetchLatestLayout: !isPublicPreview,
          currentIsReady: isReady,
          loading: {
            metadataLoading,
            layoutIsLoading,
            themeLoading,
            dropdownLoading,
          },
        });
      }

      if (!isPublicPreview) {
        if (__DBG__) console.log("[AUF:Flicker][PublishedFormPage] calling fetchLatestLayout()", { formId });
        fetchLatestLayout();
      }

      setIsReady(true);
    } else {
      console.log("❌ Not ready: Some context still loading (metadata, layout, theme, or dropdown)");
      setIsReady(false);
    }
  }, [
    formId,
    formIdReady,
    isPublicPreview,
    metadataLoading,
    layoutIsLoading,
    themeLoading,
    dropdownLoading,
    fetchLatestLayout,
  ]);


  // Memoize fields + layout to prevent re-renders if they haven't changed
  const memoizedFieldsMetadata = useMemo(() => fieldsMetadata, [fieldsMetadata]);
  const memoizedLayoutConfig = useMemo(() => layoutConfig, [layoutConfig]);

  console.log("[PublishedFormPage] Render -> Checking final skeleton condition:", {
    isReady,
    themeLoading,
    layoutIsLoading,
    dropdownLoading,
    memoizedFieldsMetadataEmpty:
      !memoizedFieldsMetadata || Object.keys(memoizedFieldsMetadata).length === 0,
  });
console.log('[PublishedFormPage] render tick');

  // Helper: read the bridge, set answer, toast, clear; returns true if consumed
const tryConsumeAufBridge = () => {
  try {
    if (__DBG__) console.log("[AUF:Flicker][PublishedFormPage] tryConsumeAufBridge() enter", { formId });

    const bridge = safeGetItem('auf_return_bridge'); // ← unwrapped value or null
    if (__DBG__) {
      console.log("[AUF:Flicker][PublishedFormPage] tryConsumeAufBridge() bridge read", {
        formId,
        hasBridge: Boolean(bridge),
        originFormId: bridge?.originFormId || null,
        hasResult: Boolean(bridge?.result),
        resultAssetId: bridge?.result?.assetId || null,
      });
    }

    if (!bridge || bridge?.originFormId !== formId) return false;
    const res = bridge?.result;
    if (!res?.assetId) return false;

    // Pick target UFI: prefer stored; else find first attach/image field
    let targetUfi = bridge.originAttachUfi || null;
    if (!targetUfi) {
      const attachFieldId = Object.values(memoizedLayoutConfig || {})
        .flat()
        .find((f) => {
          const fm = memoizedFieldsMetadata?.[f.id];
          return fm?.type === 'attach' || fm?.type === 'image';
        })?.id;
      targetUfi = attachFieldId && memoizedFieldsMetadata?.[attachFieldId]?.ufi;
    }

    if (targetUfi && typeof setAnswer === 'function') {
      if (__DBG__) {
        console.log("[AUF:Flicker][PublishedFormPage] tryConsumeAufBridge() setting answer", {
          formId,
          targetUfi,
          assetId: res.assetId,
          versionNumber: (typeof res.versionNumber === "number") ? res.versionNumber : null,
        });
      }

      setAnswer(targetUfi, {
        assetId: res.assetId,
        ...(typeof res.versionNumber === 'number' ? { versionNumber: res.versionNumber } : {})
      });

      if (__DBG__) console.log("[AUF:Flicker][PublishedFormPage] tryConsumeAufBridge() clearing bridge key", { formId });
      safeRemoveItem('auf_return_bridge'); // ← clears both session & local

      showToast('Asset added from Asset Upload Form.', 'success');
      return true;
    }
  } catch (e) {
    console.warn('[PublishedFormPage] Failed to consume AUF return bridge:', e);
  }
  return false;
};


  // ─────────────────────────────────────────────────────────────
  // Flow 3: consume AUF return bridge on origin page (non-AUF only)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (formDetails?.isAssetUploadForm === true) return; // AUF itself shouldn't consume
    tryConsumeAufBridge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, memoizedLayoutConfig, memoizedFieldsMetadata, formDetails?.isAssetUploadForm]);

  // Also consume the bridge when AUF submits and dispatches an event (no full reload)
  useEffect(() => {
    if (formDetails?.isAssetUploadForm === true) return;
    const onAufReturn = () => { tryConsumeAufBridge(); };
    window.addEventListener('auf:asset-returned', onAufReturn);
    return () => window.removeEventListener('auf:asset-returned', onAufReturn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

useEffect(() => {
  // Only when AUF is rendered inside the modal overlay
  if (!(inModal && formDetails?.isAssetUploadForm === true)) return;

  // ENTERPRISE:
  // Do NOT clear navbar_auf_bridge on mount.
  // We need it during submit to know the originRoute (e.g. '/assets').
  // We clear it ONLY after we successfully dispatch asset:created (see submit handler).
  try {
    const nb = safeGetItem('navbar_auf_bridge');
    if (nb) {
      console.log('[PublishedFormPage] navbar_auf_bridge present (keeping until submit).', {
        originRoute: nb?.originRoute || null,
        requestPick: nb?.requestPick ?? null,
      });
    }
  } catch (e) {
    console.warn('[PublishedFormPage] Failed to read navbar_auf_bridge:', e);
  }
}, [inModal, formDetails?.isAssetUploadForm]);

  // Final skeleton check
  // ────────────────────────────────────────────────────────────
  // NEW: if the AUF is rendered in the modal overlay, do NOT gate on the skeleton —
  // render immediately and let contexts catch up in the background.
  const allowInstantModalAuf =
    inModal && formDetails?.isAssetUploadForm === true;

// ✅ PublishedFormPage should gate on *form data readiness*, not AuthContext.
// RequireAccess already handles auth/ACL routing.
if (
  !allowInstantModalAuf &&
  (
    // keep authLoading ONLY if you truly need currentUser for something inside skeleton/navbar.
    // For public forms, authLoading should not block rendering.
    (!isPublicPreview && (!isReady || themeLoading || layoutIsLoading || dropdownLoading || metadataLoading))
  )
) {
    console.warn("⏳ [PublishedFormPage] Skipping render: data not fully loaded yet.");
  console.log("[PublishedFormPage] gate-check (render-return)", {
    formId,
    formIdReady,
    initialized,
    authLoading,
    isReady,
    metadataLoading,
    layoutIsLoading,
    themeLoading,
    dropdownLoading,
    isPublicPreview,
    inModal,
    allowInstantModalAuf,
  });

    return (
      <>
        {/* Hide navbar when in AUF modal */}
        {!inModal && <NavbarComponent currentUser={currentUser} />}
        <ThemeProvider theme={muiTheme}>
          {/* ⚠️ wrapper is present even during skeleton */}
          <div data-testid="form-preview-root">
            <Box
              sx={{
                backgroundColor: muiTheme.palette.background.default,
                color          : muiTheme.palette.text.primary,
                fontFamily     : muiTheme.typography.fontFamily,
                padding        : muiTheme.spacing(1),
                width          : "100%",
                minHeight      : "100vh",
              }}
            >
              <FormPreviewSkeleton />
            </Box>
          </div>
        </ThemeProvider>
      </>
    );
  }




  // All data is ready:
  console.log(" [PublishedFormPage] Final fieldsMetadata:", memoizedFieldsMetadata);
  console.log(" [PublishedFormPage] layoutConfig:", memoizedLayoutConfig);

  // Prepare the submission UI (e.g. a "DisplayCardPreview" or nothing)
  const SubmissionUI = getSubmissionUIComponent();

  // Robust type checks (survive minification)
  const uiIsCard  = SubmissionUI && SubmissionUI.type === DisplayCardPreview;
  const uiIsTable = SubmissionUI && SubmissionUI.type === DisplayTablePreview;

  // Overall flags the template already expects
  const isDisplayInCard = isDisplayInCardEnabled || uiIsCard;
  const isDisplayTable  = uiIsTable;


// AUF-in-dialog UX: override button text
// Only "Attach/Attaching" when the AUF dialog was opened FROM A FORM (Flow 3).
// If opened from navbar/home (global upload), keep "Submit/Submitting".
const isAufInDialog = formDetails?.isAssetUploadForm === true && inModal;

// Detect Flow 3 (opened from a form) vs navbar/home (global)
let flow3FromForm = false;
let flow3FromNavbar = false;

if (__DBG__) {
  console.log("[AUF:Flicker][PublishedFormPage] origin-detect (render)", {
    formId,
    inModal,
    isAufInDialog,
  });
}

try {
  const bridge = safeGetItem('auf_return_bridge'); // ← unwrapped value
  flow3FromForm = !!(isAufInDialog && bridge?.originFormId);

  if (__DBG__) {
    console.log("[AUF:Flicker][PublishedFormPage] origin-detect bridge (render)", {
      formId,
      hasBridge: Boolean(bridge),
      originFormId: bridge?.originFormId || null,
      flow3FromForm,
    });
  }
} catch (e) {
  if (__DBG__) console.warn("[AUF:Flicker][PublishedFormPage] origin-detect bridge error", e);
}

try {
  const nb = safeGetItem('navbar_auf_bridge');     // ← unwrapped value
  flow3FromNavbar = !!(isAufInDialog && nb && nb.requestPick === false);

  if (__DBG__) {
    console.log("[AUF:Flicker][PublishedFormPage] origin-detect navbar (render)", {
      formId,
      hasNavbarBridge: Boolean(nb),
      requestPick: nb?.requestPick ?? null,
      flow3FromNavbar,
    });
  }
} catch (e) {
  if (__DBG__) console.warn("[AUF:Flicker][PublishedFormPage] origin-detect navbar error", e);
}

// Label policy:
// - Flow 3 from form → "Attach/Attaching"
// - Navbar/global → keep defaults ("Submit/Submitting")
const submitCtaLabel     = flow3FromForm ? 'Attach' : undefined;
const submitLoadingLabel = flow3FromForm ? 'Attaching' : 'Submitting';

// Debug: which origin path did we detect?
console.log('[PublishedFormPage] AUF origin flags:', {
  inModal,
  isAufInDialog,
  flow3FromForm,
  flow3FromNavbar,
});


  /**
   * Find the first attach/image field in current answers and return its value.
   * Supports:
   *  - { kind:'file', file: File, ... } (AUF deferred)
   *  - { assetId } / { kind:'asset', assetId }
   *  - File (legacy)
   *  - Array of any of the above (multi)
   */
  // Look in the canonical submission array (answers-by-UFI),
  // and then fall back to fieldChanges if needed.
  const resolveAttachPayload = () => {
    try {
      const arr = typeof buildSubmissionArray === 'function' ? buildSubmissionArray() : [];

      // Prefer any attach/image fields present in layout
      const attachOrImage = arr.find((f) => f?.type === 'attach' || f?.type === 'image');
      if (attachOrImage && attachOrImage.value) return attachOrImage.value;
    } catch {}

    // Fallback (older published widgets that still wrote into fieldChanges)
    if (fieldsMetadata && fieldChanges) {
      for (const fid of Object.keys(fieldsMetadata)) {
        const meta = fieldsMetadata[fid];
        if (meta?.type === 'attach' || meta?.type === 'image') {
          const v = fieldChanges?.[fid]?.value ?? null;
          if (v) return v; // might be File, base64, array, or an asset ref
        }
      }
    }
    return null;
  };

  /**
   * Build a sanitized AUF submission array: never send File/Blob to Firestore.
   * If overrideAssetRef is provided, force the attach field to that asset ref.
   * Otherwise, if the attach value is a File, strip it to null (safe no-op).
   */
  const buildSanitizedAufSubmission = (overrideAssetRef = null) => {
    const arr = typeof buildSubmissionArray === 'function' ? buildSubmissionArray() : [];
    return arr.map((item) => {
      if (item?.type === 'attach' || item?.type === 'image') {
        if (overrideAssetRef && overrideAssetRef.assetId) {
          return {
            ...item,
            value: {
              assetId: overrideAssetRef.assetId,
              ...(typeof overrideAssetRef.versionNumber === 'number'
                ? { versionNumber: overrideAssetRef.versionNumber }
                : {}),
            },
          };
        }
        // If the current value contains File/Blob, remove it (prevent Firestore error)
        const v = item?.value;

        // Multi: array of deferred files/refs
        if (Array.isArray(v)) {
          const stripped = v.map((x) => {
            if (!x) return x;
            const hasFile =
              (x?.file && (
                ((typeof File !== 'undefined') && (x.file instanceof File)) ||
                ((typeof Blob !== 'undefined') && (x.file instanceof Blob))
              ));
            // Strip the file pointer but keep lightweight metadata if present
            if (hasFile) {
              const { file, ...rest } = x;
              return { ...rest };
            }
            return x;
          });

          // If any element had a file pointer, return stripped array
          const changed = stripped.some((x, i) => x !== v[i]);
          return changed ? { ...item, value: stripped } : item;
        }

        // Single: { kind:'file', file: File/Blob, ... }
        const hasFile =
          v && v.file && (
            ((typeof File !== 'undefined') && (v.file instanceof File)) ||
            ((typeof Blob !== 'undefined') && (v.file instanceof Blob))
          );

        if (hasFile) {
          const { file, ...rest } = v;
          return { ...item, value: rest }; // keep JSON-safe bits
        }
      }
      return item;
    });
  };

  /**
   * Standalone AUF (not dialog): write a submission record so we can
   * find the asset + tags later. Flow 3 should not use this.
   */
 const finalizeStandaloneAufSubmission = async (overrideAssetRef = null) => {
    try {
     // Build a sanitized payload that never contains File objects
     const submissionData = buildSanitizedAufSubmission(overrideAssetRef);
      console.log('[PublishedFormPage] AUF standalone → saving submission record...', submissionData);

      await handleSubmit(submissionData, { userId: currentUser?.uid });

      console.log(
        `[PublishedFormPage] AUF ${inModal ? 'dialog' : 'standalone'} → submission saved.`
      );

    } catch (e) {
      console.warn('[PublishedFormPage] AUF standalone → saving submission failed:', e);
    }
  };



/**
 * AUF-only submit handler:
 * - flips isSubmitting to disable the button
 * - drives spinner + "Submitting" text inside the button
 * - reverts state when finished
 */
const handleAufSubmit = async () => {
  if (isSubmitting) return; // guard double-clicks
  setIsSubmitting(true);

  // Enterprise-grade: freeze immediately to protect submit latency
  try { window.dispatchEvent(new Event('assetdetails:request-submit')); } catch {}

  try {
    const payload = resolveAttachPayload();
    if (!payload) {
      console.error("[PublishedFormPage] No file/asset found in answers.");
      showToast("Please attach a file before submitting.", "warning");
      return;
    }
    // ─────────────────────────────────────────────────────────────

    // ─────────────────────────────────────────────────────────────
    // ENTERPRISE: normalize payload
    // - If multi-attach ever returns an array, prefer the first usable item
    // - Keeps AUF forward-compatible without changing current schemas
    // ─────────────────────────────────────────────────────────────
    const normalizedPayload = Array.isArray(payload) ? (payload.find(Boolean) || null) : payload;
    if (!normalizedPayload) {
      console.error("[PublishedFormPage] Attach payload was empty after normalization.");
      showToast("Please attach a file before submitting.", "warning");
      return;
    }

// Existing asset selected from Library → just tag it
const selectedAssetId =
  (normalizedPayload && (normalizedPayload.assetId || normalizedPayload.id)) || null;

const isExistingAsset =
  typeof selectedAssetId === 'string' ||
  (normalizedPayload?.kind === 'asset' &&
    typeof (normalizedPayload.assetId || normalizedPayload.id) === 'string');

if (isExistingAsset) {
  try {
    const raw = sessionStorage.getItem('auf_return_bridge');

    if (raw) {
      // Flow 3 handoff: return the selection to the origin form
      const bridge = JSON.parse(raw);
      try {
        await tagAssetWithForm(selectedAssetId, bridge.originFormId);
      } catch (e) {
        console.warn("[PublishedFormPage] tagAssetWithForm (origin) failed:", e);
      }

      // ENTERPRISE: record ingest event (non-blocking)
      try {
        (async () => {
          try {
            await recordAssetIngestEvent({
              assetId: selectedAssetId,
              organizationId: formDetails?.organizationId || orgIdFromAuth || '',
              aufFormId: formId,
              originFormId: bridge?.originFormId || null,
              originAttachUfi: bridge?.originAttachUfi || null,
              mode: 'flow3',
              ingestSubmissionId: null,
              userId: currentUser?.uid || null,
              submitterSessionId: null,
              canonicalName: null,
              assetType: null,
              versionNumber: null,
              status: null,
              tags: [],
              responses: {}, // existing asset selection has no new AUF responses
            });
          } catch (_) {}
        })();
      } catch (_) {}

      sessionStorage.setItem('auf_return_bridge', JSON.stringify({
        ...bridge,
        result: { assetId: selectedAssetId, versionNumber: null }
      }));
      if (bridge.originRoute) {
        // notify origin page to consume immediately (no reload)
        try { window.dispatchEvent(new Event('auf:asset-returned')); } catch {}
        // close dialog without reload when in modal; otherwise full navigation
        if (inModal) navigate(-1);
        else window.location.assign(bridge.originRoute);
        return; // done
      }
    } else {
      // No handoff → user picked an existing asset inside AUF normally
      await tagAssetWithForm(selectedAssetId, formId);
      showToast("Asset linked to this form.", "success");

      // ENTERPRISE: record ingest event (non-blocking)
      try {
        (async () => {
          try {
            await recordAssetIngestEvent({
              assetId: selectedAssetId,
              organizationId: formDetails?.organizationId || orgIdFromAuth || '',
              aufFormId: formId,
              originFormId: null,
              originAttachUfi: null,
              mode: inModal ? 'navbar' : 'standalone',
              ingestSubmissionId: null,
              userId: currentUser?.uid || null,
              submitterSessionId: null,
              canonicalName: null,
              assetType: null,
              versionNumber: null,
              status: null,
              tags: [],
              responses: {},
            });
          } catch (_) {}
        })();
      } catch (_) {}

      // ───────────────────────────────────────────────────────────────
      // ENTERPRISE CHANGE:
      // Do NOT persist AUF submission records in the critical path.
      // If you still want an AUF “history table”, use assetIngestEvents
      // (written above) or enqueue an async submission job.
      // ───────────────────────────────────────────────────────────────
      // (removed blocking await finalizeStandaloneAufSubmission(...) here)

    }

  } catch (e) {
    console.error("[PublishedFormPage] Tag/link existing asset failed:", e);
    showToast("Failed to link asset. Please try again.", "error");
  }
  return;
}


// New local file upload (preferred shape)
if (normalizedPayload?.kind === 'file' && normalizedPayload.file instanceof File) {
  const meta = await submitAssetFromForm({
    file: normalizedPayload.file,
    formId,
    layoutConfig: memoizedLayoutConfig,
    fieldsMetadata: memoizedFieldsMetadata,
    dropdownOptions,
    fieldChanges,
    validationErrors,
    submissionData:
      typeof buildSubmissionArray === 'function'
        ? buildSubmissionArray()
        : undefined,
  });
  if (meta) {
    console.log("[PublishedFormPage] DAM upload complete:", meta);
    showToast("Asset uploaded successfully.", "success");

    // 🔹 Hoist durable identifiers once so we can reuse consistently below
    const uploadedAssetId   = meta.assetId || meta.id;
    const uploadedVersionNo = (typeof meta.versionNumber === 'number') ? meta.versionNumber : null;

    // ───────────────────────────────────────────────────────────────
    // ENTERPRISE: Notify /assets page (which may be mounted behind AUF dialog)
    // so it can prepend the newly created asset without a full refresh.
    // - This is specifically for the navbar AUF modal flow (originRoute: /assets).
    // - Safe no-op elsewhere.
    // ───────────────────────────────────────────────────────────────
    try {
      const nb = safeGetItem('navbar_auf_bridge'); // { originRoute, requestPick, createdAt } | null
      const originRoute = nb?.originRoute || null;

      // Only broadcast when the origin is the assets index route.
      if (originRoute === '/assets' && uploadedAssetId) {
        const assetForGrid = {
          // normalize for AssetCard/AssetBoard expectations
          id: uploadedAssetId,
          assetId: uploadedAssetId,

          name: meta?.name || 'New asset',
          type: meta?.type || 'image',
          assetType: meta?.assetType ?? meta?.systemAssetType ?? null,

          // IMPORTANT:
          // meta.thumbUrl from your backend is already a usable URL in many cases.
          // Keep it; AssetCard prefers thumbUrl for first paint.
          thumbUrl: meta?.thumbUrl || null,

          // keep useful UI metadata for layout/overlay
          thumb: meta?.thumb || null,
          dimensions: meta?.dimensions || meta?.metadata?.dimensions || null,
          size: meta?.size ?? null,

          // helps stable ordering in UI (AssetBoard orders by createdAt desc server-side)
          createdAt: new Date(),
          createdAtMs: Date.now(),

          // optional: keep versions if present (drawer/preview fallbacks)
          versions: meta?.versions || null,
          canonicalName: (meta?.canonicalName || meta?.name || '').toLowerCase(),
        };

        window.dispatchEvent(
          new CustomEvent('asset:created', {
            detail: {
              originRoute,
              assetId: uploadedAssetId,
              asset: assetForGrid,
              createdAtMs: Date.now(),
            },
          })
        );

        // Optional: store a one-shot bridge in case /assets mounts after close
        safeSetItem('assets_return_bridge', {
          originRoute,
          assetId: uploadedAssetId,
          asset: assetForGrid,
          createdAtMs: Date.now(),
        });

        // ENTERPRISE: one-shot — we've consumed the navbar bridge for this upload.
        // If the user uploads again later, Navbar will write a fresh bridge.
        safeRemoveItem('navbar_auf_bridge');
      }
    } catch (e) {
      console.warn('[PublishedFormPage] asset:created dispatch failed (non-fatal):', e);
    }

    // --- AUF polish: swap deferred file to durable asset ref in answers ---
    try {
      // find the attach/image field in the current layout
      const attachFieldId = Object.values(memoizedLayoutConfig || {})
        .flat()
        .find((f) => {
          const fm = memoizedFieldsMetadata?.[f.id];
          return fm?.type === "attach" || fm?.type === "image";
        })?.id;

      // resolve UFI; setAnswer accepts fieldId or UFI (UFI is canonical)
      const attachUfi =
        attachFieldId && memoizedFieldsMetadata?.[attachFieldId]?.ufi;

      if (uploadedAssetId && attachUfi && typeof setAnswer === "function") {
        setAnswer(attachUfi, {
          assetId: uploadedAssetId,
          ...(uploadedVersionNo != null ? { versionNumber: uploadedVersionNo } : {})
        });
      }
    } catch (swapErr) {
      console.warn(
        "[PublishedFormPage] AUF swap to durable ref failed (non-fatal):",
        swapErr
      );
    }

    // ENTERPRISE: record ingest event (non-blocking)
    // - This replaces AUF "submission record" writes in the critical path.
    // - AUF is an ingest workflow, not a submissions workflow.
    try {
      const bridge = safeGetItem('auf_return_bridge'); // null/undefined when not Flow 3

      // Fire-and-forget so we never block the user’s submit/attach UX
      (async () => {
        try {
          await recordAssetIngestEvent({
            assetId: uploadedAssetId,
            organizationId: formDetails?.organizationId || orgIdFromAuth || '',
            aufFormId: formId,

            // Flow-3 origin details (if present)
            originFormId: bridge?.originFormId || null,
            originAttachUfi: bridge?.originAttachUfi || null,

            // Mode semantics
            mode: bridge?.originFormId ? 'flow3' : (inModal ? 'navbar' : 'standalone'),

            ingestSubmissionId: meta?.metadata?.ingestSubmissionId || null,
            userId: currentUser?.uid || null,
            submitterSessionId: null,

            canonicalName: meta?.canonicalName || null,
            assetType: meta?.assetType ?? null,
            versionNumber: (typeof uploadedVersionNo === 'number') ? uploadedVersionNo : null,
            status: meta?.status || null,
            tags: Array.isArray(meta?.tags) ? meta.tags : [],
            responses: (meta?.metadata?.responses && typeof meta.metadata.responses === 'object')
              ? meta.metadata.responses
              : {},
          });
        } catch (_) {}
      })();
    } catch (_) {}


    /* ─────────────────────────────────────────────────────────────
       NEW: Flow 3 return bridge (handoff back to origin form)
       - if auf_return_bridge exists, tag asset with originFormId
         and redirect back with the result embedded
       ──────────────────────────────────────────────────────────── */
    try {
      const raw = sessionStorage.getItem('auf_return_bridge');
      if (raw) {
        const bridge = JSON.parse(raw);
        if (uploadedAssetId && bridge?.originFormId) {
          try {
            await tagAssetWithForm(uploadedAssetId, bridge.originFormId);
          } catch (e) {
            console.warn('[PublishedFormPage] tagAssetWithForm (origin) failed:', e);
          }
          sessionStorage.setItem(
            'auf_return_bridge',
            JSON.stringify({
              ...bridge,
              result: {
                assetId      : uploadedAssetId,
                versionNumber: uploadedVersionNo,
              },
            })
          );
          if (bridge.originRoute) {
            try { window.dispatchEvent(new Event('auf:asset-returned')); } catch {}
            if (inModal) navigate(-1);
            else window.location.assign(bridge.originRoute);
            return; // stop after close/redirect
          }
        }
      }
    } catch (e) {
      console.warn('[PublishedFormPage] AUF return bridge write/redirect failed:', e);
    }

  }

  return;
}

    // Defensive: bare File (older widgets)
    if (normalizedPayload instanceof File) {
      const meta = await submitAssetFromForm({
        file: normalizedPayload,
        formId,
        layoutConfig: memoizedLayoutConfig,
        fieldsMetadata: memoizedFieldsMetadata,
        dropdownOptions,
        fieldChanges,
        validationErrors,
        submissionData:
          typeof buildSubmissionArray === 'function'
            ? buildSubmissionArray()
            : undefined,
      });
  if (meta) {
    console.log("[PublishedFormPage] DAM upload complete:", meta);
    showToast("Asset uploaded successfully.", "success");

    // --- AUF polish: swap deferred file to durable asset ref in answers ---
    try {
      const assetId = meta.assetId || meta.id;

      const attachFieldId = Object.values(memoizedLayoutConfig || {})
        .flat()
        .find((f) => {
          const fm = memoizedFieldsMetadata?.[f.id];
          return fm?.type === "attach" || fm?.type === "image";
        })?.id;

      const attachUfi =
        attachFieldId && memoizedFieldsMetadata?.[attachFieldId]?.ufi;

      if (assetId && attachUfi && typeof setAnswer === "function") {
      setAnswer(attachUfi, {
        assetId,
        ...(typeof meta.versionNumber === 'number' ? { versionNumber: meta.versionNumber } : {})
      });
      }
    } catch (swapErr) {
      console.warn(
        "[PublishedFormPage] AUF swap to durable ref failed (non-fatal):",
        swapErr
      );
    }

    // ENTERPRISE: record ingest event (non-blocking)
    // - Keeps AUF history without writing formSubmissions.
    // - Mirrors the structured payload used in the preferred { kind:'file' } branch.
    try {
      const bridge = safeGetItem('auf_return_bridge'); // null/undefined when not Flow 3

      // Hoist durable identifiers once (avoid repeated meta lookups)
      const uploadedAssetIdLegacy = meta.assetId || meta.id;
      const uploadedVersionNoLegacy =
        (typeof meta.versionNumber === 'number') ? meta.versionNumber : null;

      // Fire-and-forget so we never block the user’s submit/attach UX
      (async () => {
        try {
          await recordAssetIngestEvent({
            assetId: uploadedAssetIdLegacy,
            organizationId: formDetails?.organizationId || orgIdFromAuth || '',
            aufFormId: formId,

            // Flow-3 origin details (if present)
            originFormId: bridge?.originFormId || null,
            originAttachUfi: bridge?.originAttachUfi || null,

            // Mode semantics
            mode: bridge?.originFormId ? 'flow3' : (inModal ? 'navbar' : 'standalone'),

            // Best-effort: uploader may have embedded this id into metadata
            ingestSubmissionId: meta?.metadata?.ingestSubmissionId || null,
            userId: currentUser?.uid || null,
            submitterSessionId: null,

            canonicalName: meta?.canonicalName || null,
            assetType: meta?.assetType ?? null,
            versionNumber:
              (typeof uploadedVersionNoLegacy === 'number') ? uploadedVersionNoLegacy : null,
            status: meta?.status || null,
            tags: Array.isArray(meta?.tags) ? meta.tags : [],
            responses: (meta?.metadata?.responses && typeof meta.metadata.responses === 'object')
              ? meta.metadata.responses
              : {},
          });
        } catch (_) {}
      })();
    } catch (_) {}

        // ───────────────────────────────────────────────────────────────
        // ENTERPRISE: AUF is an ingest workflow, not a submissions workflow.
        // Do NOT write formSubmissions records here.
        // Use assetIngestEvents (recordAssetIngestEvent) for AUF history.
        // ───────────────────────────────────────────────────────────────

   /* ─────────────────────────────────────────────────────────────
      NEW: Flow 3 return bridge (handoff back to origin form)
      - if auf_return_bridge exists, tag asset with originFormId
        and redirect back with the result embedded
      ──────────────────────────────────────────────────────────── */
   try {
     const raw = sessionStorage.getItem('auf_return_bridge');
     if (raw) {
       const bridge = JSON.parse(raw);
       const linkedAssetId = meta.assetId || meta.id;
       if (linkedAssetId && bridge?.originFormId) {
         try {
           await tagAssetWithForm(linkedAssetId, bridge.originFormId);
         } catch (e) {
           console.warn('[PublishedFormPage] tagAssetWithForm (origin) failed:', e);
         }
         sessionStorage.setItem(
           'auf_return_bridge',
           JSON.stringify({
             ...bridge,
             result: {
               assetId: linkedAssetId,
               versionNumber: (typeof meta.versionNumber === 'number') ? meta.versionNumber : null,
             },
           })
         );
         if (bridge.originRoute) {
           try { window.dispatchEvent(new Event('auf:asset-returned')); } catch {}
           if (inModal) navigate(-1);
           else window.location.assign(bridge.originRoute);
           return; // stop after close/redirect
         }

       }
     }
   } catch (e) {
     console.warn('[PublishedFormPage] AUF return bridge write/redirect failed:', e);
   }

  }

      return;
    }

    console.error("[PublishedFormPage] Unsupported attach payload:", normalizedPayload);
    showToast("Unsupported attachment. Please try again.", "warning");
  } catch (e) {
    console.error("[PublishedFormPage] Submit error:", e);
    showToast("Submission failed. Please try again.", "error");

  } finally {
    setIsSubmitting(false);

    try {
      // clear any prior pending timer (prevents stale unfreeze from earlier attempts)
      if (unfreezeTimerRef.current) {
        clearTimeout(unfreezeTimerRef.current);
        unfreezeTimerRef.current = null;
      }

      // ✅ Prefer paint-aligned unfreeze rather than a fixed delay.
      // Two RAFs = after the next paint in most browsers.
      const raf = (cb) => (typeof window !== "undefined" && window.requestAnimationFrame)
        ? window.requestAnimationFrame(cb)
        : setTimeout(cb, 0);

      raf(() => {
        raf(() => {
          try { window.dispatchEvent(new Event("assetdetails:submit-status")); } catch {}
        });
      });
    } catch (_) {}
  }
};


  return (
    <>
      {/* Navbar visible for fully-loaded form view */}
   {!inModal && <NavbarComponent currentUser={currentUser} />}

      <ThemeProvider theme={muiTheme}>
       <div data-testid="form-preview-root">

        <Box
          sx={{
            backgroundColor: muiTheme.palette.background.default,
            color: muiTheme.palette.text.primary,
            fontFamily: muiTheme.typography.fontFamily,
            padding: muiTheme.spacing(1),
            width: "100%",
            minHeight: "100vh", // Ensures we see the background color fill the viewport
          }}
        >
          {selectedTheme === "Paper" ? (
            // Keep Paper's normal theme-based padding (no p override).
            // We'll only remove padding for the DisplayCard portion itself.
            <Paper>
              {memoizedLayoutConfig && Object.keys(memoizedLayoutConfig).length > 0 ? (
                <>
                  <PreviewFields
                    fieldsMetadata={memoizedFieldsMetadata}
                    layoutConfig={memoizedLayoutConfig}
                  />

                  {memoizedLayoutConfig.submitRow &&
                    memoizedLayoutConfig.submitRow.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 1 }}>
                        <SubmitButtonComponent
                          field={memoizedLayoutConfig.submitRow[0]}
                          // AUF-only: delegate to handleAufSubmit, else keep regular flow
                          onClick={async () => {
                            if (formDetails?.isAssetUploadForm === true) {
                              await handleAufSubmit();
                            } else {
                              await handleSubmit();
                            }
                          }}
                          // Drive inline spinner + disabled inside the button (AUF only)
                          loading={formDetails?.isAssetUploadForm === true && isSubmitting}
                         loadingLabel={submitLoadingLabel}
                         label={submitCtaLabel}

                        />

                      </Box>
                    )}

                  {SubmissionUI && (
                    // If it's DisplayCardPreview, wrap it in a negative margin container
                    isDisplayInCard || isDisplayTable ? (
                      <Box
                        sx={{
                          // Paper might have ~2rem padding from your FormThemeConfig,
                          // so do negative margin that cancels it out horizontally.
                          // For example, if your theme uses 2rem (32px), set mx: -2rem or theme.spacing(4).
                          mt: 2,
                          mx: "-2rem", // Adjust to match your theme's actual padding
                        }}
                      >
                        {SubmissionUI}
                      </Box>
                    ) : (
                      // Otherwise, just show it normally
                      <Box sx={{ marginTop: 2 }}>{SubmissionUI}</Box>
                    )
                  )}
                </>
              ) : (

                <Typography variant="body1">No fields found in layout.</Typography>
              )}
            </Paper>
          ) : (
            <Box
              sx={{
                color: muiTheme.palette.text.primary,
                fontFamily: muiTheme.typography.fontFamily,
                padding: muiTheme.spacing(2),
                width: "100%",
                minHeight: "100vh",
              }}
            >
              <PreviewFields
                fieldsMetadata={memoizedFieldsMetadata}
                layoutConfig={memoizedLayoutConfig}
              />
              {memoizedLayoutConfig.submitRow &&
                memoizedLayoutConfig.submitRow.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
                    <SubmitButtonComponent
                      field={memoizedLayoutConfig.submitRow[0]}
                      onClick={async () => {
                        if (formDetails?.isAssetUploadForm === true) {
                          await handleAufSubmit();
                        } else {
                          await handleSubmit();
                        }
                      }}
                      loading={formDetails?.isAssetUploadForm === true && isSubmitting}
                       loadingLabel={submitLoadingLabel}
                       label={submitCtaLabel}
                    />

                  </Box>
                )}

              {SubmissionUI && (
                <Box sx={{ marginTop: 2 }}>{SubmissionUI}</Box>
              )}
            </Box>
          )}
        </Box>
        </div>
          {/* Global toast for submit/attach messages (non-blocking, a11y-friendly) */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{
            // Kill default max-widths that cause clipping
            '& .MuiSnackbarContent-root': {
              maxWidth: 'none',
            },
            '& .MuiPaper-root': {
              maxWidth: 'none',
            },
          }}
        >
          <Alert
            elevation={3}
            variant="filled"
            severity={toastSeverity}
            onClose={() => setToastOpen(false)}
            role="status"
            sx={{
              // size to content on one line; cap to viewport - 32px gutters
              width: 'fit-content',
              maxWidth: 'calc(100vw - 32px)',
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.75,
              // keep everything on one line
              whiteSpace: 'nowrap',
              // ensure inner message doesn't re-wrap or shrink
              '& .MuiAlert-message': {
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'inherit',
                flex: '0 0 auto',
              },
              // keep icon / action from squeezing content
              '& .MuiAlert-icon, & .MuiAlert-action': {
                flex: '0 0 auto',
              },
            }}
          >
            {toastMsg}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </>
  );
};

export default React.memo(PublishedFormPage);
